import React, { useState } from "react";
import { useProfile } from "../contexts/ProfileContext";

// Utility function to format time ago
const formatTimeAgo = (timestamp) => {
  const now = new Date();
  const created = new Date(timestamp);
  const diffInSeconds = Math.floor((now - created) / 1000);

  if (diffInSeconds < 60) {
    return "just now";
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return `${diffInDays}d ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths}mo ago`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears}y ago`;
};

function ProfileSelection() {
  const {
    profiles,
    loading,
    error,
    createProfile,
    setSelectedProfile,
    wordlists,
    wordlistsLoading,
  } = useProfile();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    gender: "male",
    wordlistId: "",
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  if (loading || wordlistsLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="alert alert-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const handleProfileClick = (profile) => {
    setSelectedProfile(profile.profile_id);
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateError("");

    const newProf = await createProfile(formData);
    if (!newProf) {
      setCreateError("Could not create profile");
    } else {
      setSelectedProfile(newProf.profile_id);
      setIsModalOpen(false);
      setFormData({ name: "", gender: "male", wordlistId: "" });
    }
    setCreating(false);
  };

  return (
    <div className="max-w-2xl mx-auto p-6 w-full">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-2xl font-bold text-[#7C6EEE]">Select a Profile</h2>
        <button
          className="btn normal-case text-lg gap-1 bg-[#FF52B4] hover:bg-[#FF52B4]/80 text-white border-none"
          onClick={() => setIsModalOpen(true)}
        >
          <span className="text-xl">+</span> New
        </button>
      </div>

      {/* Profiles List */}
      <div className="space-y-3">
        {profiles.length > 0 ? (
          profiles.map((profile) => (
            <div
              key={profile.profile_id}
              className="bg-[#1E1E1E] rounded-2xl p-4 cursor-pointer hover:bg-[#2A2A2A] transition-colors"
              onClick={() => handleProfileClick(profile)}
            >
              <div className="flex items-center gap-4">
                <div className="avatar">
                  <div className="w-14 h-14 rounded-full ring-2 ring-[#7C6EEE] ring-offset-base-100 ring-offset-2">
                    <img
                      src={profile.avatar_url || "https://placehold.co/100"}
                      alt="avatar"
                      className="rounded-full"
                    />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-medium text-white mb-1">
                    {profile.name}
                  </h3>
                  <p className="text-gray-400">
                    Created {formatTimeAgo(profile.created_at)}
                  </p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-gray-400">
            <p>No profiles found. Create one to get started!</p>
          </div>
        )}
      </div>

      {/* CREATE NEW PROFILE MODAL */}
      {isModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box relative w-full max-w-md">
            <button
              className="btn btn-sm btn-circle absolute right-2 top-2"
              onClick={() => setIsModalOpen(false)}
            >
              ✕
            </button>
            <h3 className="text-2xl font-bold mb-4">Create New Profile</h3>

            {createError && (
              <div className="alert alert-error mb-4">
                <span>{createError}</span>
              </div>
            )}

            <form onSubmit={handleCreateProfile}>
              <div className="form-control mb-4">
                <label className="label">Name</label>
                <input
                  type="text"
                  className="input input-bordered"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-control mb-4">
                <label className="label">Gender</label>
                <div className="flex gap-4">
                  <label className="label cursor-pointer gap-2">
                    <input
                      type="radio"
                      name="gender"
                      className="radio radio-primary"
                      value="male"
                      checked={formData.gender === "male"}
                      onChange={(e) =>
                        setFormData({ ...formData, gender: e.target.value })
                      }
                    />
                    <span className="label-text">Male</span>
                  </label>
                  <label className="label cursor-pointer gap-2">
                    <input
                      type="radio"
                      name="gender"
                      className="radio radio-primary"
                      value="female"
                      checked={formData.gender === "female"}
                      onChange={(e) =>
                        setFormData({ ...formData, gender: e.target.value })
                      }
                    />
                    <span className="label-text">Female</span>
                  </label>
                </div>
              </div>

              <div className="form-control mb-4">
                <label className="label">Word List</label>
                <select
                  className="select select-bordered w-full"
                  value={formData.wordlistId}
                  onChange={(e) =>
                    setFormData({ ...formData, wordlistId: e.target.value })
                  }
                  required
                >
                  <option value="">Select a word list</option>
                  {wordlists.map((list) => (
                    <option key={list.wordlist_id} value={list.wordlist_id}>
                      {list.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-action">
                <button
                  type="button"
                  className="btn btn-ghost mr-2"
                  onClick={() => setIsModalOpen(false)}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creating}
                >
                  {creating ? "Creating..." : "Create Profile"}
                </button>
              </div>
            </form>
          </div>
          <div
            className="modal-backdrop bg-black/50"
            onClick={() => setIsModalOpen(false)}
          ></div>
        </div>
      )}
    </div>
  );
}

export default ProfileSelection;
